import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faAngleLeft, faAngleRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SendDialog, SendDialogData } from './send-dialog/send-dialog.component';
import { ReceiptsService } from '../receipts/receipts.service';
import { ToastrService } from 'ngx-toastr';
import { SendDocumentModel } from './send-dialog/send-document.model';
import { TranslateService } from '@ngx-translate/core';
import { ImageService } from 'src/app/_common/services/image.service';

export interface PreviewDialogData {
  isReceipt: boolean,
  image: string,
  fileId: number,
  items: { fileId: number, type: number }[]
}

@Component({
  selector: 'preview-dialog',
  templateUrl: './preview.dialog.html',
  styleUrls: ['./preview.dialog.scss']
})

export class PreviewDialog implements OnInit, OnDestroy {

  @Output() active: EventEmitter<any> = new EventEmitter<any>();

  faTimes = faTimes;
  faNext = faAngleRight;
  faPrevious = faAngleLeft;

  imageUrl: SafeResourceUrl;
  imageBlob: Blob;
  imageWidth: number;
  imageScale: number = 1.2;

  nextId: number = null;
  previousId: number = null;

  sendDialogData: SendDialogData = { isEmail: false, email: null, phone: null }

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<PreviewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: PreviewDialogData,
    private sanitizer: DomSanitizer,
    private receiptService: ReceiptsService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private imageService: ImageService
  ) {
    this.imageBlob = new Blob([this.data.image], { type: 'image/svg+xml' });
    this.imageWidth = this.getImageWidthInPx(this.data.image);
  }

  ngOnInit(): void {
    this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.imageBlob));
    this.getPreviousAndNext(this.data.fileId);
  }

  ngOnDestroy(): void {
    URL.revokeObjectURL(this.imageUrl.toString());
  }

  onNext(): void {
    this.loadImage(this.nextId);
  }

  onPrevious(): void {
    this.loadImage(this.previousId);
  }

  onPrint(width: number): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = URL.createObjectURL(this.imageBlob);
    document.body.appendChild(iframe);

    iframe.onload = function () {
      const svgDocument = iframe.contentDocument.documentElement;

      svgDocument.style.width = width + 'mm';
      svgDocument.style.height = 'auto';

      iframe.contentWindow.print();
    }
  }

  openSendDialog(type: string) {
    if (type == 'email') {
      this.sendDialogData.isEmail = true;
      let dialogRef = this.dialog.open(SendDialog, {
        width: '380px',
        data: this.sendDialogData,
        autoFocus: true
      });

      dialogRef.afterClosed().subscribe((data: SendDialogData) => {
        if (data) {
          let sendModel: SendDocumentModel = { fileId: this.data.fileId, email: data.email, phone: data.phone }
          this.receiptService.send(sendModel).subscribe(async response => {
            if (response.model) {
              this.toastr.success(this.translateService.instant('common.email-sent'));
            }
            else {
              this.toastr.error(response.error.message);
            }
          });
        }
      });
    }
  }

  private loadImage(fileId: number) {
    this.imageService.getImage(fileId, this.getType(fileId)).subscribe(async (response) => {
      if (response.status === 204) {
        this.toastr.error(this.translateService.instant('documents.preview-dialog.load-error'));
        return;
      }

      this.imageWidth = this.getImageWidthInPx(response.body);
      this.imageBlob = new Blob([response.body], { type: 'image/svg+xml' });
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.imageBlob));
      this.active.emit(fileId);
      this.data.fileId = fileId;
      this.getPreviousAndNext(fileId);
    });
  }

  private getImageWidthInPx(svgString: string): number {
    const viewBoxMatch = svgString.match(/viewBox="([^"]+)"/);
    if (viewBoxMatch && viewBoxMatch[1]) {
      const viewBoxParts = viewBoxMatch[1].split(' ');
      return parseFloat(viewBoxParts[2]);
    }
    return 0;
  }

  getImageWidthInMm(widthInPx: number): number {
    return widthInPx * (75 / 300);
  }

  private getPreviousAndNext(fileId: number): void {
    let currentIndex = this.data.items.findIndex(item => item.fileId === fileId);

    if (currentIndex === -1) { return; }

    this.previousId = this.getPreviousIndex(currentIndex);
    this.nextId = this.getNextIndex(currentIndex);
  }

  private getPreviousIndex(currentIndex: number) {
    let previousIndex = -1;
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (this.data.items[i].fileId !== null) {
        return this.data.items[i].fileId;
      }
    }
    return previousIndex;
  }

  private getNextIndex(currentIndex: number) {
    let nextIndex = -1;
    for (let i = currentIndex + 1; i <= this.data.items.length - 1; i++) {
      if (this.data.items[i].fileId !== null) {
        return this.data.items[i].fileId;
      }
    }
    return nextIndex;
  }

  private getType(fileId: number) {
    return this.data.items.find(item => item.fileId === fileId)?.type;
  }
}
